import { useEffect, useRef, useState } from 'react';
import { phaseNumberOf } from '../viewerPipelines';
import { ExternalLink, Pencil } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useDragScroll } from '../useDragScroll';
import { MeshThumb } from '../MeshThumb';
import type { PartSize } from '../partsRun';
import { formatPartSize } from '../formatUnits';
import { meshSrc, meshStamp } from '../threeDApi';
import { pictureSrc } from '../pictureRef';
import type { Job3D } from '../threeDApi';

// A thumbnail sized to a target AREA rather than a fixed height, capped at the row
// height, so a wide seat and a slim leg read as comparable pictures instead of the
// wide one dwarfing the rest. Until it loads it is capped by max-h-20 (no huge flash);
// once its aspect ratio is known, its height is set so width × height ≈ AREA.
const THUMB_AREA = 5184; // px² — a square thumbnail is ~72px, and nothing exceeds 80px.
const THUMB_MAX_H = 80; // px — the row height, which equal-area sizing must never change.

function AreaThumb({ src }: { src: string }) {
  const [height, setHeight] = useState<number | null>(null);
  const ref = useRef<HTMLImageElement>(null);
  const size = (im: HTMLImageElement) => {
    if (!im.naturalWidth || !im.naturalHeight) return;
    const ratio = im.naturalWidth / im.naturalHeight; // w / h
    // area = ratio·h² ⇒ h = √(area / ratio), never taller than the row.
    setHeight(Math.min(THUMB_MAX_H, Math.sqrt(THUMB_AREA / ratio)));
  };
  // The catalog preloads every picture before it renders the table, so by the time this
  // mounts the bytes are cached — and a load event that fires before React attaches its
  // onLoad handler is missed. Size from the already-complete image on mount so a cached
  // thumbnail lands at its final size with no reflow.
  useEffect(() => {
    if (ref.current?.complete) size(ref.current);
  }, [src]);
  return (
    <img
      ref={ref}
      src={src}
      alt=""
      onLoad={(e) => size(e.currentTarget)}
      style={height ? { height: `${height}px` } : undefined}
      className="max-h-20 w-auto rounded bg-white"
    />
  );
}

// One drawing of a part, and the notice (manual) it was cropped from.
//
// `docId` is that manual's id (its SHA), `docName` its file name, and `slug` its friendly
// /view/<slug> identity (brand-category-item). All three are null when the manual has
// since been deleted, or was never recorded (the schematics that predate this). A part
// has one of these per manual it turned up in.
export type Schematic = {
  id: number;
  docId: string | null;
  docName: string | null;
  slug: string | null;
};

// A real photograph of a part: its source notice (like a schematic) plus the
// image-generation model that drew it (null for hand-uploaded or pre-model photos).
// The Phase 2 generated photos land here, tagged with both.
export type Photo = {
  id: number;
  docId: string | null;
  docName: string | null;
  slug: string | null;
  model: string | null;
  // The run (seq number) of the source manual this photo was generated in; null for
  // hand-uploaded / pre-run-tracking photos.
  runSeq: number | null;
  // Whether that run was a DEV run (true) or a PROD run (false); null when unknown.
  runFromDev: boolean | null;
  // The notice listing-photo ids actually sent to the generator as context; null for
  // photos generated before this was recorded (their real context is unknown).
  contextIds: number[] | null;
  // The submodel this photo shows: its ID, and the label to display for it. Null for
  // hardware, tools and hand-added photos — the same object in every colour, so they
  // belong to no submodel and stay visible under all of them.
  variant: string | null;
  variantLabel: string | null;
  // Which side of the part it shows, and which pipeline the run belonged to. Both null on
  // a photo made before they were recorded — and `view` is null for every LLM-pipeline
  // photo besides, that pipeline having no angle to ask for.
  view: string | null;
  runMode: string | null;
};

// A saved part/tool in the reusable catalog (metadata + the ids to fetch its
// images; the bytes come from their own endpoints).
// Where a part's name links to: the manual it was read from, open on the Parts phase with
// this part selected. The reverse of the "FOUND IN DATABASE — YES" link in that very
// table, so the two pages point at each other instead of one pointing at the other.
//
// The FIRST notice that names one, and a part in three manuals has three equally true
// answers. Rather than make the row ask which, it goes to one of them: the phase's own
// selector then names every part of that manual, and getting to a different manual is
// what the row's pictures are captioned with. A part whose manuals have all been deleted
// keeps its name as plain text — there is nowhere to go.
export function partSource(item: CatalogItem): string | null {
  for (const s of [...item.schematics, ...item.photos]) {
    if (s.slug) return s.slug;
    if (s.docId) return s.docId;
  }
  return null;
}

export type CatalogItem = {
  id: number;
  uuid: string;
  kind: 'part' | 'tool';
  nameEn: string;
  nameFr: string;
  ref: string;
  // Top-level folder, e.g. "Chairs". Blank = uncategorized (grouped last).
  category: string;
  // How big the part is, as the manual gave it: measured axes, or a fastener's thread and
  // overall length. Null for the rows saved before the database had a column for it.
  size: PartSize | null;
  schematics: Schematic[];
  photos: Photo[];
  createdAt: string | null;
};

// The bytes of one schematic, by its id.
export const schematicSrc = (id: number) => pictureSrc({ source: 'schematic', imageId: id });
// The bytes of one real photograph, by its id.
export const photoSrc = (id: number) => pictureSrc({ source: 'photo', imageId: id });

// The Schéma cell: every drawing kept for the part, each a button that opens it zoomed
// with its source. Fixed min-height so equal-area sizing (which makes wide drawings
// shorter) never shrinks the row.
function SchematicCell({
  schematics,
  cellCls,
  onZoom,
  t,
}: {
  schematics: Schematic[];
  cellCls: string;
  onZoom: (s: Schematic) => void;
  t: (k: string) => string;
}) {
  return (
    <td className={cellCls}>
      {schematics.length > 0 && (
        <div className="flex min-h-[5rem] flex-wrap items-center justify-center gap-1.5">
          {schematics.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => onZoom(s)}
              title={s.docName || t('notice.catalog.schematicUnknownSource')}
              className="rounded transition-opacity hover:opacity-80"
            >
              <AreaThumb src={schematicSrc(s.id)} />
            </button>
          ))}
        </div>
      )}
    </td>
  );
}

// The Photos réelles cell: the part's real photographs as a sliding history, dragged
// left/right, each opening zoomed with its source and a way to remove it.
function PhotoCell({
  photos,
  cellCls,
  onZoom,
  t,
}: {
  photos: Photo[];
  cellCls: string;
  onZoom: (p: Photo) => void;
  t: (k: string) => string;
}) {
  const photosRef = useDragScroll<HTMLDivElement>();
  return (
    <td className={cellCls}>
      {photos.length > 0 && (
        <div
          ref={photosRef}
          // w-fit (not justify-center): centered via mx-auto while the photos fit, but
          // once they overflow it scrolls from the START — justify-center would push the
          // first photo's left edge off-screen where the scroll can't reach it.
          className="mx-auto flex w-fit max-w-[15rem] cursor-grab gap-1.5 overflow-x-auto"
        >
          {photos.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => onZoom(p)}
              title={p.docName || t('notice.catalog.schematicUnknownSource')}
              className="shrink-0 rounded transition-opacity hover:opacity-80"
            >
              <img
                src={photoSrc(p.id)}
                alt=""
                className="h-20 w-auto rounded border border-slate-200 bg-white"
              />
            </button>
          ))}
        </div>
      )}
    </td>
  );
}

// The 3D cell: the meshes kept for this part, each opening turnable.
//
// A drawing and a photograph are contributed BY a manual; a reconstruction is of the
// part itself — it was made from photographs that may have come from several manuals,
// and filing it under one of them would be a claim nobody made.
export function ModelCell({ models, cellCls, onOpen }: {
  models: Job3D[];
  cellCls: string;
  onOpen: (job: Job3D) => void;
}) {
  const ref = useDragScroll<HTMLDivElement>();
  return (
    <td className={`${cellCls} align-middle`}>
      {models.length > 0 && (
        // Same overflow rule as the photo cell: centered while they fit, scrolling from
        // the start once they do not.
        <div ref={ref} className="mx-auto flex w-fit max-w-[15rem] cursor-grab gap-1.5 overflow-x-auto py-1">
          {models.map((j) => (
            <MeshThumb
              key={j.id}
              src={meshSrc(j.id, meshStamp(j))}
              selected={false}
              // Every mesh in this cell is in the database by definition — that is what
              // put it here — so the badge would mark all of them and distinguish none.
              inCatalog={false}
              label={`#${j.id}`}
              onSelect={() => onOpen(j)}
            />
          ))}
        </div>
      )}
    </td>
  );
}

// One item in the Parts DB table: ONE ROW PER PART — id, name, its drawings, its
// photographs, its 3D models, the pencil.
//
// It used to be one row per notice the part appears in, each captioned with that
// manual's identity in a Notice column. The caption is gone (the manual a picture came
// from is still stored, still filters this table, and still shows on a picture's hover
// and in its zoom — it is simply not a column), and with it the reason to split the
// rows: without the caption, a part in three manuals drew three rows that looked
// identical and explained nothing. Its pictures sit together in one cell instead.
//
// Its own file because NoticeCatalog was over the size limit — and because a row is a
// thing: it drags, it links, it opens. The panel keeps the state; this draws it.
export function CatalogItemRow({
  item,
  models,
  rowRef,
  linked,
  dragging,
  nameCols,
  cellCls,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  onEdit,
  onZoomSchematic,
  onZoomPhoto,
  onOpenModel,
  t,
}: {
  item: CatalogItem;
  // This part's meshes in the Parts DB, newest first.
  models: Job3D[];
  // Set only on the row a ?item= link points at, so the panel can scroll to it.
  rowRef?: React.Ref<HTMLTableRowElement>;
  linked: boolean;
  dragging: boolean;
  nameCols: { label: string; get: (it: CatalogItem) => string }[];
  cellCls: string;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDragOver: () => void;
  onDrop: () => void;
  onEdit: () => void;
  onZoomSchematic: (s: Schematic) => void;
  onZoomPhoto: (p: Photo) => void;
  onOpenModel: (job: Job3D) => void;
  t: (k: string) => string;
}) {
  // The row carries the drag gesture — dragging it moves the part between category
  // folders — and the drag/linked tinting. It is also the scroll target of a ?item=
  // link: tinted and centered, because "it is on screen somewhere" is not an answer to
  // "which one".
  const rowCls = `cursor-grab ${dragging ? 'opacity-40' : ''} ${
    linked ? 'bg-blue-50' : ''
  }`;
  // The manual to open this part in, and where to come back to.
  const source = partSource(item);
  const here = useLocation();
  const from = encodeURIComponent(`${here.pathname}${here.search}`);
  return (
    <tr
      ref={rowRef}
      className={rowCls}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        // Firefox starts no drag at all unless the dataTransfer carries something, so
        // this line is the difference between the gesture working and doing nothing
        // there. The id is what a drop means anyway; the panel reads it from its own
        // state, so this payload is for the browser, not for us.
        e.dataTransfer.setData('text/plain', String(item.id));
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver();
      }}
      onDrop={(e) => {
        e.preventDefault();
        onDrop();
      }}
    >
      {/* Short UUID (first segment) to save space; full value on hover. */}
      <td
        title={item.uuid}
        className={`${cellCls} whitespace-nowrap align-middle font-mono text-xs text-slate-500`}
      >
        {item.uuid ? item.uuid.split('-')[0] : ''}
      </td>
      {nameCols.map((c, ci) => (
        <td
          key={c.label}
          className={`${cellCls} align-middle text-slate-800`}
        >
          {/* The FIRST name column is the link, not all of them: two columns pointing at
              one page is one link drawn twice, and the English name is the one this table
              is sorted and searched by. */}
          {ci === 0 && source ? (
            <Link
              to={`/view/${source}?phase=${phaseNumberOf('notice-step-2')}&item=${item.id}&from=${from}`}
              className="inline-flex items-center gap-1 text-blue-700 underline decoration-dotted underline-offset-4 transition-colors hover:text-blue-600"
            >
              {c.get(item)}
              <ExternalLink className="h-3 w-3 shrink-0" />
            </Link>
          ) : (
            c.get(item)
          )}
        </td>
      ))}
      {/* How big the part is, in the same layout the reader's parts table uses, so one
          part reads the same on both pages. Blank rather than absent when nothing was
          saved: a row from before the database had a size column is a part nobody has
          measured, which is a thing to see. */}
      <td
        className={`${cellCls} whitespace-pre-line align-middle text-center font-mono text-[11px] leading-relaxed text-slate-600`}
      >
        {/* The BLOCK is centred and its lines are left-aligned within it, which is what
            the reader's parts table does with the same four lines: centring each line
            individually would ragged both edges and stop "x:", "y:", "z:" lining up, and
            leaving the block left put it against the border with the column's width
            trailing off to the right. */}
        <span className="inline-block text-left">
          {formatPartSize(item.size, item.category)}
        </span>
      </td>
      <SchematicCell schematics={item.schematics} cellCls={cellCls} onZoom={onZoomSchematic} t={t} />
      {/* One real-images cell: every model's photos together (the per-model split is
          now the counts shown above the table). Each photo still carries its model,
          shown when opened zoomed. */}
      <PhotoCell photos={item.photos} cellCls={cellCls} onZoom={onZoomPhoto} t={t} />
      <ModelCell models={models} cellCls={cellCls} onOpen={onOpenModel} />
      <td className={`${cellCls} align-middle`}>
        <button
          type="button"
          onClick={onEdit}
          title={t('notice.catalog.editItem')}
          aria-label={t('notice.catalog.editItem')}
          className="rounded p-1 text-slate-400 transition-colors hover:text-blue-500"
        >
          <Pencil className="h-4 w-4" />
        </button>
      </td>
    </tr>
  );
}
